import * as React from 'react';
import { useState } from 'react';
import { AccountView, AccountViewProps } from './Account.view';
import { useUserData } from '../../../hooks/useUserData';
import { useUserRole } from '../../../hooks/useUserRole';
import { ApolloError } from '@apollo/client';
import {
  CombinedOrganizationSettingsInput,
  DateFormatSetting,
  StartOfWeekFormatSetting,
  TimeFormatSetting,
  TimezoneSetting,
  UpdateCombinedOrganizationSettingsMutationFn,
  UpdateUserOnlineMarketingMutationFn,
  UpdateWorkspaceMutationFn
} from '../../../models/graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';
import { SettingsType, useSettings } from '../../../hooks/useSettings';
import { FormElementAction } from '../../../models/basic-objects';

export type AccountControllerProps = {
  updateCombinedOrganizationSettings: UpdateCombinedOrganizationSettingsMutationFn;
  updateUserOnlineMarketing: UpdateUserOnlineMarketingMutationFn;
  updateWorkspace: UpdateWorkspaceMutationFn;
};

export const AccountController = (props: AccountControllerProps): JSX.Element => {
  const { userData } = useUserData();
  const { selectedOrganization, selectedWorkspace } = useSelectedOrganization();
  const [openPasswordModal, setOpenPasswordModal] = useState<boolean>(false);
  const [openNameEmailModal, setOpenNameEmailModal] = useState<boolean>(false);
  const [openDotMenu, setOpenDotMenu] = useState<boolean>(false);
  const { selectedOrgRole } = useUserRole();
  const settings: SettingsType = useSettings();
  const selectedTimezone = selectedWorkspace?.timezone ?? TimezoneSetting.UTC;

  const timezoneSelectAction: FormElementAction<string> = (value: string, complete) => {
    const tz = TimezoneSetting[value as TimezoneSetting];
    if (selectedWorkspace && tz) {
      props.updateWorkspace({
        variables: {
          workspaceId: selectedWorkspace.id,
          input: {
            timezone: tz
          }
        }
      });
    }

    complete(true);
  };

  const timeFormatSelectAction: FormElementAction<TimeFormatSetting> = (value: TimeFormatSetting, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        timeFormat: value
      };
      props
        .updateCombinedOrganizationSettings({
          variables: { input: input, organizationId: selectedOrganization.id, userId: userData?.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error updating your time format',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const startOfWeekSelectAction: FormElementAction<StartOfWeekFormatSetting> = (
    value: StartOfWeekFormatSetting,
    complete
  ) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        startOfWeekFormat: value
      };
      props
        .updateCombinedOrganizationSettings({
          variables: { input: input, organizationId: selectedOrganization.id, userId: userData?.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error updating the start of the week',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const dateFormatSelectAction: FormElementAction<DateFormatSetting> = (value: DateFormatSetting, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        dateFormat: value
      };
      props
        .updateCombinedOrganizationSettings({
          variables: { input: input, organizationId: selectedOrganization.id, userId: userData?.id }
        })
        .then(() => {
          complete(true);
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error updating your date format',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    }
  };

  const productAnalyticsToggleAction: ToggleAction = (newVal, complete) => {
    if (selectedOrganization) {
      const input: CombinedOrganizationSettingsInput = {
        productAnalyticsEnabled: newVal
      };
      props
        .updateCombinedOrganizationSettings({
          variables: { input: input, organizationId: selectedOrganization.id, userId: userData?.id }
        })
        .then(() => {
          complete();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error changing your product analytics setting',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const onlineMarketingToggleAction: ToggleAction = (newVal, complete) => {
    if (selectedOrganization) {
      props
        .updateUserOnlineMarketing({
          variables: { onlineMarketing: newVal, userId: userData?.id }
        })
        .then(() => {
          complete();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error changing your marketing setting',
            detail: mappedError,
            severity: 'error'
          });
          complete(false);
        });
    } else {
      complete(false);
    }
  };

  const viewProps: AccountViewProps = {
    ...props,
    name: userData?.firstName || '',
    email: userData?.email || '',
    settings,
    openPasswordModal,
    setOpenPasswordModal,
    openNameEmailModal,
    setOpenNameEmailModal,
    openDotMenu,
    setOpenDotMenu,
    selectedOrgRole,
    timezoneSelectAction,
    timeFormatSelectAction,
    startOfWeekSelectAction,
    dateFormatSelectAction,
    productAnalyticsToggleAction,
    onlineMarketingToggleAction,
    selectedTimezone
  };
  return <AccountView {...viewProps} />;
};
